import React, {Fragment} from "react";
import SignaturePad from "signature_pad";
import './SignaturePad.scss';
import debounce from "../../utils/debounce";

function blobToFile(theBlob, fileName){
    //A Blob() is almost a File() - it's just missing the two properties below which we will add
    theBlob.lastModifiedDate = new Date();
    theBlob.lastModified = theBlob.lastModifiedDate.getTime();
    theBlob.name = fileName;
    return theBlob;
}

// https://stackoverflow.com/questions/6850276/how-to-convert-dataurl-to-file-object-in-javascript
function dataURLtoBlob(dataURI) {
    return new Promise((resolve, reject) => {
        try {
            // convert base64 to raw binary data held in a string
            // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
            const byteString = atob(dataURI.split(',')[1]);

            // separate out the mime component
            const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

            // write the bytes of the string to an ArrayBuffer
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            //Old Code
            //write the ArrayBuffer to a blob, and you're done
            //var bb = new BlobBuilder();
            //bb.append(ab);
            //return bb.getBlob(mimeString);

            //New Code
            resolve(new Blob([ab], {type: mimeString}));
        } catch (e) {
            reject(e);
        }
    });
}

// https://stackoverflow.com/questions/23150333/html5-javascript-dataurl-to-blob-blob-to-dataurl
function blobToDataURL(blob): Promise<string> {
    return new Promise((resolve) => {
        const a = new FileReader();
        a.onload = function(e) {
            resolve(e.target.result as string);
        };
        a.readAsDataURL(blob);
    });
}

type SignaturePadPropsType = {
    label: any,
    name?: string,
    // filename without extension
    filename?: string,
    value?: null|undefined|string,
    required?: any,
    type?: "string"|"blob"|"file",
    saveChanges?: (field: string, value: any) => void
};
export default class SignaturePadInput extends React.Component<SignaturePadPropsType, any> {
    protected canvasRef: React.RefObject<HTMLCanvasElement>;
    protected signaturePad: SignaturePad;
    protected abortLoadingValue: null|(() => void) = null;

    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
    }

    resizeCanvas() {
        if (this.canvasRef.current) {
            let canvas = this.canvasRef.current;
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext("2d").scale(ratio, ratio);

            this.signaturePad?.clear();
            this.signaturePad = new SignaturePad(this.canvasRef.current);
            this.setValue();
            this.signaturePad.addEventListener("endStroke", () => {
                this.saveChanges();
            });
        }
    }

    saveChanges = debounce(() => {
        if (this.props.saveChanges) {
            if (this.props.type === "blob" || this.props.type === "file") {
                dataURLtoBlob(this.signaturePad.toDataURL()).then((value) => {
                    if (this.props.type === "blob") {
                        this.props.saveChanges(this.props.name, value);
                    } else {
                        this.props.saveChanges(this.props.name, blobToFile(value, this.props.filename + ".png"));
                    }
                });
            } else {
                this.props.saveChanges(this.props.name, this.signaturePad.toDataURL());
            }
        }
    }, 300);

    setValue = () => {
        if (this.signaturePad) {
            if (typeof this.props.value === "string") {
                this.signaturePad.fromDataURL(this.props.value);
            } else if (this.props.value instanceof Blob) {
                let aborted = false;
                this.abortLoadingValue = () => {
                    aborted = true;
                }
                blobToDataURL(this.props.value).then((data) => {
                    if (aborted) return;
                    this.signaturePad.fromDataURL(data);
                });
            } else if(!this.props.value) {
                this.signaturePad.clear();
            }
        }
    }

    componentDidUpdate(prevProps: Readonly<SignaturePadPropsType>, prevState: Readonly<any>, snapshot?: any) {
        if (this.props.value !== prevProps.value) {
            this.setValue();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.resizeCanvas);
        this.resizeCanvas();
    }

    componentWillUnmount() {
        this.signaturePad = null;
        window.removeEventListener("resize", this.resizeCanvas);
    }

    render() {
        const style: {display?: any} = {};
        if (this.props.label === false) {
            style.display = "none";
        }
        return (
            <Fragment>
                {this.props.label !== false && <label htmlFor={this.props.name} className="control-label" style={style}>{this.props.label} {this.props.required && <span className="invalid-feedback">*</span>}</label>}
                <div className="signature-pad">
                    <canvas ref={this.canvasRef} />
                </div>
            </Fragment>
        );
    }
}